import { ROTransaction, RWTransaction, runWithROTransaction } from '../../../db'
import {
  BaseDBService,
  BaseDBServiceOpts,
  Context,
  CoreDict,
  Id,
  PaginatedResult,
  createObj,
  enumGetValues,
  getPage,
  notNull,
  runIfDefined,
  throwAccessDenied,
  throwInvalidOperation,
  throwObjectNotFound,
  validateOptionalNumber,
  validateRequiredId,
  validateRequiredString,
} from '../../../common'
import { CreateEventMode, ModelEvent, ModelEventsProcessor } from '../../events'
import { MunicipalEntitiesRepository, MunicipalEntity } from '../../municipalEntity'
import { SalesPoint, SalesPointsRepository } from '../../salesPoints'
import { User, UsersRepository } from '../../users'
import { CitizenCategory } from '../../citizens'
import {
  AddUsedCardsStat,
  GroupedUsedCardsStat,
  UsedCardsStat,
  UsedCardsStatOptions,
  UsedCardsStatValues,
  UsedCardsStatsRepository,
  UsedCardsStatsService,
} from './usedCardsStats'
import { Permissions } from '../../authorization'
import { cloneDeep, forIn, groupBy, toArray, uniq } from 'lodash'

enum EventType {
  USED_CARDS_STAT_ADD = 'USED_CARDS_STAT_ADD',
}

type AddUsedCardsStatPayload = {
  dataEntryDate: string
  salesPointId: Id
  values: string
}

interface DBUsedCardsStatsServiceOpts extends BaseDBServiceOpts<CoreDict> {
  usersRepository: UsersRepository
  salesPointsRepository: SalesPointsRepository
  municipalEntitiesRepository: MunicipalEntitiesRepository
  usedCardsStatsRepository: UsedCardsStatsRepository
}

export class DBUsedCardsStatsService
  extends BaseDBService<DBUsedCardsStatsServiceOpts, CoreDict>
  implements UsedCardsStatsService
{
  constructor(opts: DBUsedCardsStatsServiceOpts) {
    super('DBUsedCardsStatsService', opts)
    this.logger.debug({}, '<e7139821> Init')
  }

  private validateUserID = (userID?: string) => validateRequiredId('<4a8c519d> User ID', userID)
  private validateDataEntryDate = (dataEntryDate?: string) =>
    validateRequiredString('<6cd874f9> Data entry date', dataEntryDate)
  private validateSalesPointID = (salesPointID?: string) =>
    validateRequiredId('<6a95c1be> Sales point ID', salesPointID)
  private validateValue = (valueName: string, value?: number) =>
    validateOptionalNumber(valueName, value, { minValue: 0 })

  async add(ctx: Context, usedCardsStat: AddUsedCardsStat): Promise<Id> {
    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      let salesPoint: SalesPoint | undefined
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.USED_CARDS_STATS_MANAGE))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_USED_CARDS_STATS_MANAGE)
        salesPoint = await this.checkForSalesPointExists('<484e63a5>', ctx, tx, usedCardsStat.salesPointId)
        await this.checkAccessToMunicipalEntity('<688dbdd1>', ctx, tx, [salesPoint.municipalEntityId])
      }

      salesPoint ||= await this.checkForSalesPointExists('<a447e4fd>', ctx, tx, usedCardsStat.salesPointId)

      const entityId = this.opts.idGenerator.generate()

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.NEW,
        entityId,
        type: EventType.USED_CARDS_STAT_ADD,
        municipalEntityId: salesPoint.municipalEntityId,
        payload: createObj<AddUsedCardsStatPayload>({
          // TODO-111 Нужна маска на дату?
          dataEntryDate: this.validateDataEntryDate(usedCardsStat.dataEntryDate.toString()),
          salesPointId: usedCardsStat.salesPointId,
          values: JSON.stringify(this.validateValues(usedCardsStat.values)),
        }),
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])

    return event.entityId
  }

  async formReport(
    ctx: Context,
    opts?: UsedCardsStatOptions,
  ): Promise<{ groupedBySP: PaginatedResult<GroupedUsedCardsStat>; groupedByME: GroupedUsedCardsStat[] }> {
    await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.USED_CARDS_STATS_VIEW))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_USED_CARDS_STATS_VIEW)

        if (opts?.filter?.municipalEntityId)
          await this.checkAccessToMunicipalEntity('<c928271e>', ctx, tx, [opts?.filter?.municipalEntityId])
        else {
          const user = await this.checkForUserExists(ctx, tx, this.validateUserID(ctx.userId))
          opts = { ...opts, filter: { ...opts?.filter, municipalEntityId: user.municipalEntityId } }
        }
      }
    })

    const groupedBySP = await this.findAllWithGroupedBySP(opts)

    const idsME: Id[] = uniq(groupedBySP.items.map(i => i.municipalEntity?.id))
    const groupedByME: GroupedUsedCardsStat[] = await this.findByIdWithGroupedByME(idsME, opts)

    return { groupedBySP, groupedByME }
  }

  private async findAllWithGroupedBySP(
    opts?: UsedCardsStatOptions | undefined,
  ): Promise<PaginatedResult<GroupedUsedCardsStat>> {
    const groupedBySP = await runWithROTransaction(this.opts.dbConnection, async tx => {
      const dbStoredUsedCardsStats = await this.opts.usedCardsStatsRepository.findAll(tx, {
        ...opts,
        paging: undefined,
      })

      const usedCardsStats = dbStoredUsedCardsStats.items

      let groupedUsedCardsStat = await this.createGroupedUsedCardsStat(usedCardsStats)

      groupedUsedCardsStat =
        runIfDefined(opts?.filter?.municipalEntityId, municipalEntityId =>
          groupedUsedCardsStat.filter(gucs => gucs.municipalEntity.id === municipalEntityId),
        ) ?? groupedUsedCardsStat

      return toArray(groupBy(groupedUsedCardsStat, 'salesPoint.id'))
    })

    return getPage(this.sumGroupValues(groupedBySP), opts?.paging)
  }

  private async findByIdWithGroupedByME(
    ids: string[],
    opts?: UsedCardsStatOptions | undefined,
  ): Promise<GroupedUsedCardsStat[]> {
    const groupedByME = await runWithROTransaction(this.opts.dbConnection, async tx => {
      const dbStoredUsedCardsStats = await this.opts.usedCardsStatsRepository.findAll(tx, {
        ...opts,
        paging: undefined,
      })

      const usedCardsStats = dbStoredUsedCardsStats.items

      const groupedUsedCardsStat = (await this.createGroupedUsedCardsStat(usedCardsStats)).filter(
        g => g.municipalEntity && ids.includes(g.municipalEntity.id),
      )
      return toArray(groupBy(groupedUsedCardsStat, 'municipalEntity.id'))
    })

    return this.sumGroupValues(groupedByME)
  }

  private sumGroupValues(group: GroupedUsedCardsStat[][]): GroupedUsedCardsStat[] {
    return group
      .map(groupItem =>
        groupItem.reduce<GroupedUsedCardsStat | undefined>((previousValue, currentValue) => {
          if (!previousValue) return { ...currentValue }
          const newValue = cloneDeep(previousValue)

          forIn(CitizenCategory, category => {
            newValue.values[category] += currentValue.values[category]
          })
          return newValue
        }, undefined),
      )
      .filter(notNull)
  }

  private async getSalesPoints(spIds: Id[]): Promise<SalesPoint[]> {
    const result = await runWithROTransaction(this.opts.dbConnection, async tx => {
      return this.opts.salesPointsRepository.findAll(tx, { filter: { ids: spIds, withDeleted: true } })
    })

    return result.items.filter(sp => typeof sp != typeof undefined)
  }

  private async getMunicipalEntities(meIds: Id[]): Promise<MunicipalEntity[]> {
    const result = await runWithROTransaction(this.opts.dbConnection, async tx => {
      return this.opts.municipalEntitiesRepository.findAll(tx, { filter: { ids: meIds } })
    })

    return result.items.filter(me => me != undefined)
  }

  private validateValues(values: UsedCardsStatValues): UsedCardsStatValues {
    // TODO-111 Нужно проверять, что хотя бы одно значение указано?
    return {
      CH: this.validateValue('<c59ed67e> CH', values.CH) ?? 0,
      AB_M: this.validateValue('<820373b1> AB_M', values.AB_M) ?? 0,
      AB_F: this.validateValue('<37ad8f60> AB_F', values.AB_F) ?? 0,
      P_M: this.validateValue('<bbbdd813> P_M', values.P_M) ?? 0,
      P_F: this.validateValue('<1aedbb1d> P_F', values.P_F) ?? 0,
    }
  }

  private async createGroupedUsedCardsStat(usedCardsStats: UsedCardsStat[]): Promise<GroupedUsedCardsStat[]> {
    const salesPoints = await this.getSalesPoints(usedCardsStats.map(ucs => ucs.salesPointId))
    this.logger.info({}, '<dd8c5f17>', { usedCardsStats: usedCardsStats, salesPoints: salesPoints })
    const municipalEntities = await this.getMunicipalEntities(salesPoints.map(sp => sp.municipalEntityId))

    return usedCardsStats.map(ucs => {
      const salesPoint: SalesPoint =
        salesPoints.find(sp => sp.id === ucs.salesPointId) ?? throwObjectNotFound('<d38452d9> Sales point not found')

      const municipalEntity: MunicipalEntity =
        municipalEntities.find(me => me.id === salesPoint.municipalEntityId) ??
        throwObjectNotFound('<9404e023> Municipal entity not found')

      return {
        values: ucs.values,
        salesPoint,
        municipalEntity,
      }
    })
  }

  private async checkForSalesPointExists(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    id: Id,
    opts?: { withDeleted: boolean },
  ): Promise<SalesPoint> {
    if (opts?.withDeleted)
      return (
        (await this.opts.salesPointsRepository.findById(tx, id)) ??
        throwObjectNotFound(`${errorCode} ${this.translate(ctx, d => d.salesPoints.notFoundByIdError, id)}`)
      )
    else
      return (
        (await this.opts.salesPointsRepository.findByIdNonDeleted(tx, id)) ??
        throwObjectNotFound(`${errorCode} ${this.translate(ctx, d => d.salesPoints.notFoundByIdError, id)}`)
      )
  }

  private async checkAccessToMunicipalEntity(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    meIds: (string | undefined)[],
    userID?: Id,
  ): Promise<void> {
    const filteredMEIds = meIds?.filter(id => typeof id != typeof undefined)

    userID ||= this.validateUserID(ctx.userId)

    const user = await this.checkForUserExists(ctx, tx, userID)

    filteredMEIds.forEach(id => {
      if (user.municipalEntityId != id) {
        throwAccessDenied(
          `${errorCode} ${this.opts.translationService.translate(
            ctx.langCode,
            d => d.cardIssuePoints.municipalEntityAccessDenied,
          )}`,
        )
      }
    })
  }

  private async checkForUserExists(ctx: Context, tx: ROTransaction, id: Id): Promise<User> {
    return (
      (await this.opts.usersRepository.findById(tx, id)) ??
      throwObjectNotFound(`<675a42d8> ${this.translate(ctx, d => d.users.notFoundByIdError, id)}`)
    )
  }
}

export class UsedCardsStatsModelEventProcessor implements ModelEventsProcessor {
  supportedEventTypes: string[] = enumGetValues(EventType)

  constructor(private opts: { usedCardsStatsRepository: UsedCardsStatsRepository }) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    throwInvalidOperation('<f743520b> Merge not supported')
  }

  async process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type as EventType) {
      case EventType.USED_CARDS_STAT_ADD: {
        const p = event.payload as AddUsedCardsStatPayload
        await this.opts.usedCardsStatsRepository.save(tx, {
          id: event.entityId,
          createdAt: event.createdAt,
          dataEntryDate: new Date(p.dataEntryDate),
          salesPointId: p.salesPointId,
          values: p.values,
        })
        return true
      }
    }
    return false
  }
}
